import { Component, inject, signal } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { UiModule } from '../../../../components/ui.module';
import { copyToClipboard } from '../../../../public/util/navigator.utils';
import { NotifyService } from '../../../../service/notify.service';
import { AccessType, TheftReportAttachmentType } from '../../../../types';
import { resolveKioskPhotoUrl } from '../../../components/suspect-image/suspect-image.component';
import { TrackReviewActivityDirective } from '../../../directives/track-review-activity.directive';

type CellParams = ICellRendererParams;

@Component({
  selector: 'app-pan-cell-theft-report-video',
  imports: [UiModule, MatTooltipModule, RouterModule, MatIcon, TrackReviewActivityDirective],
  templateUrl: './cell-theft-report-video.component.html',
  styleUrl: './cell-theft-report-video.component.scss',
})
export class CellTheftReportVideoComponent implements ICellRendererAngularComp {
  private notifyService = inject(NotifyService);

  public photoLink = signal<string>('');
  public loadingVideosForReport = signal<boolean>(false);
  public currentVideo = signal<number>(0);

  public params!: CellParams;
  public accessType: AccessType = 'case';

  agInit(params: CellParams) {
    this.params = params;
    this.accessType = params.colDef?.cellRendererParams?.accessType ?? 'case';
    this.resolvePhotoUrl().then((url) => this.photoLink.set(url));
  }

  refresh(params: CellParams) {
    this.params = params;
    this.accessType = params.colDef?.cellRendererParams?.accessType ?? 'case';
    this.resolvePhotoUrl().then((url) => this.photoLink.set(url));
    return true;
  }

  async resolvePhotoUrl() {
    if (this.params.data.use_kiosk_photo && this.params.data.image_id) {
      return await resolveKioskPhotoUrl(this.params.data.image_id);
    }
    return this.params.data.attachments?.find((a) => a.type === TheftReportAttachmentType.InstancePhoto)?.content ?? '';
  }

  loadVideos() {
    this.loadingVideosForReport.set(true);
  }

  prevVideo() {
    this.currentVideo.set(this.currentVideo() - 1);
  }

  nextVideo() {
    this.currentVideo.set(this.currentVideo() + 1);
  }

  getVideoLink(index: number) {
    const videoLinks = this.params.data.attachments?.filter((a) => a.type === TheftReportAttachmentType.VideoLink) ?? [];
    return videoLinks.length >= index + 1 ? (videoLinks[index].content ?? '') : '';
  }

  getVideoPassword(index: number) {
    const videoLinks = this.params.data.attachments?.filter((a) => a.type === TheftReportAttachmentType.VideoLink) ?? [];
    return videoLinks.length >= index + 1 ? (videoLinks[index].password ?? '') : '';
  }

  async copyToClipboard($event: MouseEvent, text: string) {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }

    try {
      await copyToClipboard(text);
      this.notifyService.notify('Video password copied');
    } catch {
      this.notifyService.notify('Failed to copy the password to clipboard, please copy it manually');
    }

    if ($event) {
      return false;
    }
  }
}
